import React from 'react'

import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { Field, Form, Formik } from 'formik'
import ReactTooltip from 'react-tooltip'

import { NumberField } from '../FormikInputs'
import { useGetDscrQuery, useUpdateOrCreateDscrMutation } from '@/services/dscr'
import { Dscr } from '@/types/unlock'

interface DSCRCalculatorField {
  applicationUUUID: string
  applicationId: number
  overridesValue: number
  onBlur: () => void
  readOnly?: boolean
}

function DSCRCalculatorField({
  applicationUUUID,
  applicationId,
  overridesValue,
  onBlur,
  readOnly = false,
}: DSCRCalculatorField) {
  const { data: dscr } = useGetDscrQuery(applicationUUUID)
  const [updateOrCreateDscr, { isError }] = useUpdateOrCreateDscrMutation()

  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  const [isOpenRentalIncome, setIsOpenRentalIncome] = React.useState<boolean>(false)
  const [isOpenPitia, setIsOpenPitia] = React.useState<boolean>(false)

  const initialValues: Partial<Dscr> = {
    uuid: dscr?.uuid,
    application: applicationId,
    leased_unit_1: dscr?.leased_unit_1 || false,
    leased_unit_2: dscr?.leased_unit_2 || false,
    leased_unit_3: dscr?.leased_unit_3 || false,
    leased_unit_4: dscr?.leased_unit_4 || false,
    taxes_ins_included: dscr?.taxes_ins_included || false,
    monthly_unit_1: dscr?.monthly_unit_1 || 0,
    monthly_unit_2: dscr?.monthly_unit_2 || 0,
    monthly_unit_3: dscr?.monthly_unit_3 || 0,
    monthly_unit_4: dscr?.monthly_unit_4 || 0,
    mortgage_debt: dscr?.mortgage_debt || 0,
    insurance_debt: typeof dscr?.insurance_debt === 'number' ? dscr.insurance_debt : 0,
    taxes_debt: typeof dscr?.taxes_debt === 'number' ? dscr.taxes_debt : 0,
    hoa_debt: dscr?.hoa_debt || 0,
    pitia: dscr?.pitia || 0,
    rental_income: dscr?.rental_income || 0,
    is_leased: dscr?.is_leased,
  }

  const pitiaError =
    !dscr?.taxes_ins_included &&
    (!parseFloat(dscr?.insurance_debt?.toString() || '0') ||
      !parseFloat(dscr?.taxes_debt?.toString() || '0'))
  const limit = 1.25
  const dscrColor = initialValues.is_leased
    ? overridesValue >= limit
      ? 'text-success'
      : 'text-warning'
    : overridesValue >= limit
      ? 'text-warning'
      : 'text-error'

  return (
    <div className={`text-gray-400 relative`}>
      <div className='relative'>
        <NumberField
          extraClassName={dscrColor}
          parentClassName='text-black'
          name='dscr'
          onBlur={onBlur}
          readOnly={readOnly}
          disabled={true}
          decimalScale={2}
          thousandSeparator={false}
          prefix=''
          suffix='X'
        />
        <span
          className='hover:opacity-75 hover:cursor-pointer absolute right-5 top-2'
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? (
            <ChevronDownIcon
              className='text-black'
              width={28}
            />
          ) : (
            <ChevronRightIcon width={28} />
          )}
        </span>
      </div>
      {isOpen && (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={async (values: Partial<Dscr>, { setSubmitting }) => {
            const newValues: Partial<Dscr> = { ...values }
            delete newValues.pitia
            delete newValues.rental_income
            delete newValues.is_leased

            await updateOrCreateDscr(newValues)

            setSubmitting(false)
          }}
        >
          {({ values, submitForm, handleChange, isSubmitting }) => (
            <div className='absolute top-10 -left-52'>
              <Form className='p-3 absolute bg-white rounded shadow-lg z-50 min-w-[380px] text-sm border border-gray-200'>
                {isError && <span className='text-error'>Review Inputs</span>}
                <div className='flex justify-between mb-4 mt-3 font-bold'>
                  <span>Rental Income</span>
                  <span>
                    <NumberField
                      parentClassName='w-1/2 m-auto'
                      extraClassName='text-center text-sm border-none hover:shadow-none !p-1'
                      name='rental_income'
                      readOnly={true}
                      decimalScale={2}
                      thousandSeparator={true}
                      prefix='$'
                    />
                  </span>
                  <span
                    className='hover:opacity-75 hover:cursor-pointer'
                    onClick={() => setIsOpenRentalIncome(!isOpenRentalIncome)}
                  >
                    {isOpenRentalIncome ? (
                      <ChevronDownIcon
                        className='text-black'
                        width={20}
                      />
                    ) : (
                      <ChevronRightIcon width={20} />
                    )}
                  </span>
                </div>
                <div>
                  {isOpenRentalIncome && (
                    <table className='border-collapse w-full text-center mb-10'>
                      <thead>
                        <th>Units</th>
                        <th>Rental Income</th>
                        <th>Leased</th>
                      </thead>
                      <tbody>
                        <tr>
                          <td>1</td>
                          <td>
                            <NumberField
                              parentClassName='w-1/2 m-auto'
                              extraClassName='text-center text-sm border-none hover:shadow-none !p-1 !font-normal'
                              name='monthly_unit_1'
                              onBlur={submitForm}
                              readOnly={isSubmitting || readOnly}
                              decimalScale={2}
                              thousandSeparator={true}
                              prefix='$'
                            />
                          </td>
                          <td>
                            <Field
                              name='leased_unit_1'
                              className='rounded cursor-pointer'
                              type='checkbox'
                              readOnly={isSubmitting || readOnly}
                              onChange={(e) => {
                                handleChange(e)
                                submitForm()
                              }}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>2</td>
                          <td>
                            <NumberField
                              parentClassName='w-1/2 m-auto'
                              extraClassName='text-center text-sm border-none hover:shadow-none !p-1 !font-normal'
                              name='monthly_unit_2'
                              onBlur={submitForm}
                              readOnly={isSubmitting || readOnly}
                              decimalScale={2}
                              thousandSeparator={true}
                              prefix='$'
                            />
                          </td>
                          <td>
                            <Field
                              name='leased_unit_2'
                              className='rounded cursor-pointer'
                              type='checkbox'
                              readOnly={isSubmitting || readOnly}
                              onChange={(e) => {
                                handleChange(e)
                                submitForm()
                              }}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>3</td>
                          <td>
                            <NumberField
                              parentClassName='w-1/2 m-auto'
                              extraClassName='text-center text-sm border-none hover:shadow-none !p-1 !font-normal'
                              name='monthly_unit_3'
                              onBlur={submitForm}
                              readOnly={isSubmitting || readOnly}
                              decimalScale={2}
                              thousandSeparator={true}
                              prefix='$'
                            />
                          </td>
                          <td>
                            <Field
                              name='leased_unit_3'
                              className='rounded cursor-pointer'
                              type='checkbox'
                              readOnly={isSubmitting || readOnly}
                              onChange={(e) => {
                                handleChange(e)
                                submitForm()
                              }}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>4</td>
                          <td>
                            <NumberField
                              parentClassName='w-1/2 m-auto'
                              extraClassName='text-center text-sm border-none hover:shadow-none !p-1 !font-normal'
                              name='monthly_unit_4'
                              onBlur={submitForm}
                              readOnly={isSubmitting || readOnly}
                              decimalScale={2}
                              thousandSeparator={true}
                              prefix='$'
                            />
                          </td>
                          <td>
                            <Field
                              name='leased_unit_4'
                              className='rounded cursor-pointer'
                              type='checkbox'
                              readOnly={isSubmitting || readOnly}
                              onChange={(e) => {
                                handleChange(e)
                                submitForm()
                              }}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>

                <div className='flex justify-between mb-4 font-bold'>
                  <span className='w-1/4'>PITIA</span>
                  {pitiaError && (
                    <ReactTooltip
                      place={'right'}
                      id='pitiaError'
                    >
                      The insurance and taxes values are needed
                    </ReactTooltip>
                  )}
                  <span
                    data-for='pitiaError'
                    data-tip
                  >
                    <NumberField
                      parentClassName='w-1/2 m-auto'
                      extraClassName={`text-center text-sm border-none hover:shadow-none !p-1 ${
                        pitiaError ? 'text-error' : ''
                      }`}
                      name='pitia'
                      readOnly={true}
                      decimalScale={2}
                      thousandSeparator={true}
                      prefix='$'
                    />
                  </span>
                  <span
                    className='hover:opacity-75 hover:cursor-pointer'
                    onClick={() => setIsOpenPitia(!isOpenPitia)}
                  >
                    {isOpenPitia ? (
                      <ChevronDownIcon
                        className='text-black'
                        width={20}
                      />
                    ) : (
                      <ChevronRightIcon width={20} />
                    )}
                  </span>
                </div>
                <div>
                  {isOpenPitia && (
                    <table className='border-collapse w-full text-center'>
                      <thead>
                        <th className='min-w-[85px]'>Type</th>
                        <th className='min-w-[110px]'>Monthly Debt</th>
                        <th>Taxes &amp; Ins. Included</th>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Mortgage</td>
                          <td>
                            <NumberField
                              parentClassName='w-1/2 m-auto'
                              extraClassName='text-center text-sm border-none hover:shadow-none !p-1 !font-normal'
                              name='mortgage_debt'
                              onBlur={submitForm}
                              readOnly={isSubmitting || readOnly}
                              decimalScale={2}
                              thousandSeparator={true}
                              prefix='$'
                            />
                          </td>
                          <td>
                            <Field
                              name='taxes_ins_included'
                              className='rounded cursor-pointer'
                              type='checkbox'
                              readOnly={isSubmitting || readOnly}
                              onChange={(e) => {
                                handleChange(e)
                                submitForm()
                              }}
                            />
                          </td>
                        </tr>
                        <tr className={`${values.taxes_ins_included ? 'text-gray-400' : ''}`}>
                          <td>Insurance</td>
                          <td>
                            <NumberField
                              parentClassName='w-1/2 m-auto'
                              extraClassName='text-center text-sm border-none hover:shadow-none !p-1 !font-normal'
                              name='insurance_debt'
                              onBlur={submitForm}
                              readOnly={isSubmitting || values.taxes_ins_included || readOnly}
                              decimalScale={2}
                              thousandSeparator={true}
                              prefix='$'
                            />
                          </td>
                        </tr>
                        <tr className={`${values.taxes_ins_included ? 'text-gray-400' : ''}`}>
                          <td>Taxes</td>
                          <td>
                            <NumberField
                              parentClassName='w-1/2 m-auto'
                              extraClassName='text-center text-sm border-none hover:shadow-none !p-1 !font-normal'
                              name='taxes_debt'
                              onBlur={submitForm}
                              readOnly={isSubmitting || values.taxes_ins_included || readOnly}
                              decimalScale={2}
                              thousandSeparator={true}
                              prefix='$'
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>HOA</td>
                          <td>
                            <NumberField
                              parentClassName='w-1/2 m-auto'
                              extraClassName='text-center text-sm border-none hover:shadow-none !p-1 !font-normal'
                              name='hoa_debt'
                              onBlur={submitForm}
                              readOnly={isSubmitting || readOnly}
                              decimalScale={2}
                              thousandSeparator={true}
                              prefix='$'
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              </Form>
            </div>
          )}
        </Formik>
      )}
    </div>
  )
}

export default DSCRCalculatorField
